import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  createProject,
  fetchMyProjects,
  deleteProjectApi,
  duplicateProject,
  fetchFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  moveProjectToFolder,
  removeProjectFromFolder,
} from "@/lib/api";
import { formatDate } from "@/lib/dates";
import {
  ChartBar,
  Plus,
  SignOut,
  UserCircle,
  ArrowRight,
  ArrowLeft,
  Crown,
  FolderOpen,
  FolderPlus,
  Spinner,
  Lock,
  GearSix,
  X,
  SquaresFour,
  ListBullets,
  Rows,
  Check,
  WarningCircle,
} from "@phosphor-icons/react";
import PaywallDialog from "@/components/PaywallDialog";
import HaveAQuestionBadge from "@/components/HaveAQuestionBadge";
import { SupportTicketDialog } from "@/components/HaveAQuestionBadge";
import MemberAvatarStack from "@/components/MemberAvatarStack";
import ProjectCardMenu from "@/components/ProjectCardMenu";
import FolderCard from "@/components/FolderCard";
import ThemeToggle from "@/components/ThemeToggle";
import { BrandmarkHorizontal } from "@/components/Brandmark";
import NotificationsBell from "@/components/NotificationsBell";
import DashboardSidebar from "@/components/DashboardSidebar";
import useIsMobile from "@/hooks/useIsMobile";
import VelocityInsightsPanel from "@/components/VelocityInsightsPanel";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [ticketTarget, setTicketTarget] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  // Drill-down state — null means we're at the dashboard root.
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const [d, f] = await Promise.all([fetchMyProjects(), fetchFolders()]);
      setData(d);
      setFolders(f);
    } catch (e) {
      toast.error("Failed to load your projects");
    } finally {
      setLoading(false);
    }
  };

  // Mirror `data` into a ref so click-handler logic can read it without
  // triggering the experimental react-hooks/immutability rule (which flags
  // state-read + setter-dispatch in the same expression as ambiguous).
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);
  const onNewClick = useCallback(() => {
    _resolveNewClickTarget(dataRef.current, setPaywallOpen, setCreateOpen);
  }, []);

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProjectApi(deleteTarget.id);
      toast.success("Project deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const doDuplicate = async (project) => {
    if (duplicatingId) return;
    setDuplicatingId(project.id);
    try {
      await duplicateProject(project.id);
      toast.success(`Duplicated "${project.name}"`);
      load();
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywallOpen(true);
      } else {
        toast.error(e?.response?.data?.detail || "Failed to duplicate");
      }
    } finally {
      setDuplicatingId(null);
    }
  };

  // ----- Folder handlers -----
  const handleCreateFolder = async (name) => {
    try {
      const created = await createFolder({ name });
      toast.success(`Folder "${created.name}" created`);
      setFolders((prev) => [...prev, created]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create folder");
    }
  };
  const handleRenameFolder = async (id, name) => {
    try {
      const updated = await updateFolder(id, { name });
      setFolders((prev) => prev.map((f) => (f.id === id ? { ...f, ...updated } : f)));
      toast.success("Folder renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename");
    }
  };
  const handleDeleteFolder = async (id) => {
    try {
      await deleteFolder(id);
      toast.success("Folder deleted — projects are back in the main list");
      setFolders((prev) => prev.filter((f) => f.id !== id));
      if (activeFolderId === id) setActiveFolderId(null);
      // Reload projects so their folder_id resets
      const d = await fetchMyProjects();
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete folder");
    }
  };
  const moveToFolder = async (projectId, folderId) => {
    try {
      await moveProjectToFolder(folderId, projectId);
      // Optimistic UI: update the local data + folders without a full refetch
      setData((d) => ({
        ...d,
        projects: (d?.projects || []).map((p) =>
          p.id === projectId ? { ...p, folder_id: folderId } : p
        ),
      }));
      // Reload folders to refresh aggregated dates/counts
      const fresh = await fetchFolders();
      setFolders(fresh);
      toast.success("Project moved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move project");
    }
  };
  const removeFromFolder = async (projectId, folderId) => {
    try {
      await removeProjectFromFolder(folderId, projectId);
      setData((d) => ({
        ...d,
        projects: (d?.projects || []).map((p) =>
          p.id === projectId ? { ...p, folder_id: null } : p
        ),
      }));
      const fresh = await fetchFolders();
      setFolders(fresh);
      toast.success("Removed from folder");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  // Sidebar collapse — persisted across reloads. Defined BEFORE the loading
  // early-return so the hook order stays stable on every render.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("tg_dashboard_sidebar") === "1",
  );
  useEffect(() => {
    localStorage.setItem("tg_dashboard_sidebar", sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);

  // Mobile: sidebar is hidden by default and only shows when the hamburger
  // is tapped (slide-in drawer). Closes on backdrop tap or after a nav action.
  const isMobile = useIsMobile(768);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Per-section view toggles (thumbnail vs list), each remembered separately.
  const [ownedView, setOwnedView] = usePersistedView("tg_owned_view");
  const [folderView, setFolderView] = usePersistedView("tg_folder_view");
  const [sharedView, setSharedView] = usePersistedView("tg_shared_view");

  if (authLoading || loading) {
    return (
      <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500 text-sm font-mono-ibm">
          <Spinner size={18} weight="bold" className="animate-spin" />
          Loading…
        </div>
      </div>
    );
  }

  const owned = data?.projects.filter((p) => p.is_owner) || [];
  const shared = data?.projects.filter((p) => !p.is_owner) || [];
  // Drill-down view: when activeFolderId is set, only show that folder's projects.
  const activeFolder = folders.find((f) => f.id === activeFolderId) || null;
  const ownedTopLevel = activeFolder
    ? owned.filter((p) => p.folder_id === activeFolderId)
    : owned.filter((p) => !p.folder_id);
  const limit = data?.limit;
  const limitLabel = limit === null ? "∞" : limit;
  const ownedLayout = activeFolder ? folderView : ownedView;
  const setOwnedLayout = activeFolder ? setFolderView : setOwnedView;

  // ---- Aggregated metrics for the top-right + velocity insights cards ----
  // Active projects = projects the user can see and that aren't archived.
  // Total tasks = sum of task counts across visible projects.
  // Open / overdue are computed server-side (per /my/projects payload) so the
  // efficiency dial reflects real data rather than a placeholder.
  const totalProjects = data?.projects.length || 0;
  const totalTasks = (data?.projects || []).reduce((s, p) => s + (p.task_count || 0), 0);
  const openTasks = data?.total_open ?? (data?.projects || []).reduce((s, p) => s + (p.open_count || 0), 0);
  const overdueTasks = data?.total_overdue ?? (data?.projects || []).reduce((s, p) => s + (p.overdue_count || 0), 0);
  const efficiencyPct = openTasks > 0
    ? Math.max(0, Math.min(100, Math.round(((openTasks - overdueTasks) / openTasks) * 100)))
    : 100;
  const activeTimeLabel = totalProjects > 0 ? `${totalProjects} proj` : "—";

  return (
    <div className="min-h-screen w-full bg-slate-50 flex" data-testid="dashboard">
      {/* Sidebar: persistent on desktop, drawer on mobile. */}
      {isMobile ? (
        mobileSidebarOpen && (
          <>
            <div
              className="fixed inset-0 bg-slate-950/40 z-40"
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-dashboard-backdrop"
            />
            <div className="fixed top-0 bottom-0 left-0 z-50 bg-white shadow-xl">
              <DashboardSidebar
                collapsed={false}
                onToggleCollapsed={() => setMobileSidebarOpen(false)}
                user={user}
                onNewProject={() => { setMobileSidebarOpen(false); onNewClick(); }}
                onOpenMilestones={() => { setMobileSidebarOpen(false); toast.info("Milestones view coming soon"); }}
                onOpenSupport={() => { setMobileSidebarOpen(false); navigate("/contact"); }}
              />
            </div>
          </>
        )
      ) : (
        <DashboardSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
          user={user}
          onNewProject={onNewClick}
          onOpenMilestones={() => toast.info("Milestones view coming soon")}
          onOpenSupport={() => navigate("/contact")}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — auth widgets only; brand lives in the sidebar now */}
        <div className="h-16 px-4 md:px-8 flex items-center justify-between md:justify-end border-b border-slate-200 bg-white sticky top-0 z-10">
          {/* Mobile-only hamburger to open the sidebar drawer */}
          {isMobile && (
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              className="h-10 w-10 rounded-md border border-slate-300 flex items-center justify-center text-slate-600"
              aria-label="Open menu"
              data-testid="dashboard-mobile-menu-btn"
            >
              <Rows size={16} weight="bold" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <NotificationsBell />
            <UserMenu user={user} onLogout={async () => { await logout(); navigate("/"); }} />
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto w-full px-4 md:px-8 py-6 md:py-8 flex-1">
          {/* Top-row metrics + welcome + CTAs.
              Welcome column flexes; CTAs sit at its right edge, immediately
              to the left of the Active Projects metric. */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_220px_220px] gap-4 mb-8 items-end">
            <div className="flex flex-col">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-slate-300 rounded-md text-[10px] uppercase tracking-widest text-slate-600 mb-3 self-start font-sf-label">
                <TierBadge tier={data?.tier} />
                <span className="text-slate-400">·</span>
                <span>{data?.owned_count || 0} / {limitLabel} owned</span>
              </div>
              {activeFolder ? (
                <>
                  <button
                    type="button"
                    onClick={() => setActiveFolderId(null)}
                    className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-950 uppercase tracking-widest mb-3 self-start font-sf-label"
                    data-testid="folder-back-btn"
                  >
                    <ArrowLeft size={11} weight="bold" />
                    Back to all projects
                  </button>
                  <h1 className="font-display text-4xl tracking-tight text-slate-950 flex items-center gap-3">
                    <span className="h-9 w-9 rounded-md flex items-center justify-center text-white" style={{ background: activeFolder.color || "#241623" }}>
                      <FolderOpen size={18} weight="fill" />
                    </span>
                    {activeFolder.name}
                  </h1>
                </>
              ) : (
                <>
                  <h1 className="font-display text-4xl tracking-tight text-slate-950">
                    Welcome, {user?.name?.split(" ")[0] || "there"}
                  </h1>
                  <p className="text-sm text-slate-500 mt-1.5">
                    Group projects into folders, drag them in or out, and we&apos;ll roll up the timeline.
                  </p>
                  <div className="flex items-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setFolderDialogOpen(true)}
                      className="h-11 rounded-md border-slate-300 hover:border-slate-950 text-slate-700 font-medium px-4"
                      data-testid="new-folder-btn"
                    >
                      <FolderPlus size={14} weight="bold" />
                      <span className="ml-2">New Folder</span>
                    </Button>
                    <Button
                      onClick={onNewClick}
                      className="h-11 rounded-md text-white font-medium px-5"
                      style={{ background: "#4f56ff" }}
                      data-testid="new-project-btn"
                    >
                      {data?.at_limit ? <Lock size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
                      <span className="ml-2">
                        {data?.at_limit ? "Upgrade to create more" : "New Project"}
                      </span>
                    </Button>
                  </div>
                </>
              )}
            </div>
            <MetricCard
              label="Active projects"
              value={totalProjects}
              testid="metric-active-projects"
            />
            <MetricCard
              label="Total tasks"
              value={totalTasks}
              testid="metric-total-tasks"
            />
          </div>

          {/* Section title row (CTAs moved into the welcome column above) */}
          <div className="flex items-center justify-between gap-2 mb-6">
            <h2 className="font-display text-2xl tracking-tight text-slate-950">
              {activeFolder ? `Folder: ${activeFolder.name}` : "Your workspace"}
            </h2>
            {activeFolder && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setFolderDialogOpen(true)}
                  className="h-11 rounded-md border-slate-300 hover:border-slate-950 text-slate-700 font-medium px-4"
                  data-testid="new-folder-btn-folder-view"
                >
                  <FolderPlus size={14} weight="bold" />
                  <span className="ml-2">New Folder</span>
                </Button>
                <Button
                  onClick={onNewClick}
                  className="h-11 rounded-md text-white font-medium px-5"
                  style={{ background: "#4f56ff" }}
                  data-testid="new-project-btn-folder-view"
                >
                  {data?.at_limit ? <Lock size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
                  <span className="ml-2">
                    {data?.at_limit ? "Upgrade to create more" : "New Project"}
                  </span>
                </Button>
              </div>
            )}
          </div>

          {/* 2-col layout: main column (folders / yours / shared) + right rail (velocity + activity) */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6">
            <div className="space-y-10 min-w-0">
              {/* Folders row — root view only */}
              {!activeFolder && folders.length > 0 && (
                <Section
                  title="Folders"
                  count={folders.length}
                  testid="folders-section"
                  empty={null}
                >
                  {folders.map((f) => {
                    const nested = owned.filter((p) => p.folder_id === f.id);
                    return (
                      <FolderCard
                        key={f.id}
                        folder={{ ...f, project_count: nested.length }}
                        nestedProjects={nested}
                        onOpen={() => setActiveFolderId(f.id)}
                        onDelete={() => setDeleteFolderTarget(f)}
                        onRename={() => setRenameTarget(f)}
                        onDropProject={(pid) => moveToFolder(pid, f.id)}
                        onReportIssue={() => setTicketTarget({ id: f.id, name: `Folder: ${f.name}` })}
                      />
                    );
                  })}
                </Section>
              )}

              {/* Drop-here strip for moving projects OUT of a folder back to root */}
              {activeFolder && (
                <FolderRootDropZone
                  onDropProject={(pid) => removeFromFolder(pid, activeFolder.id)}
                />
              )}

              {/* Owned (or current-folder contents) */}
              <Section
                title={activeFolder ? "Projects in this folder" : "Your projects"}
                count={ownedTopLevel.length}
                testid="owned-section"
                layout={ownedLayout}
                rightSlot={
                  ownedTopLevel.length > 0 ? (
                    <ViewToggle value={ownedLayout} onChange={setOwnedLayout} prefix={activeFolder ? "folder" : "owned"} />
                  ) : null
                }
                empty={
                  <EmptyState
                    title={activeFolder ? "Empty folder" : "No projects yet"}
                    sub={
                      activeFolder
                        ? "Drag a project card here from the dashboard to put it in this folder."
                        : "Create your first Gantt chart to get started."
                    }
                    cta={
                      !activeFolder && (
                        <Button
                          onClick={onNewClick}
                          className="h-10 rounded-md text-white"
                          style={{ background: "#4f56ff" }}
                          data-testid="empty-new-btn"
                        >
                          <Plus size={14} weight="bold" />
                          <span className="ml-2">New Project</span>
                        </Button>
                      )
                    }
                  />
                }
              >
                {ownedTopLevel.map((p) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    draggable
                    compact={ownedLayout === "list"}
                    onOpen={() => navigate(`/project/${p.id}`)}
                    onDelete={() => setDeleteTarget(p)}
                    onDuplicate={() => doDuplicate(p)}
                    onReportIssue={() => setTicketTarget(p)}
                    onMoveOut={p.folder_id ? () => removeFromFolder(p.id, p.folder_id) : null}
                    canDelete
                    canDuplicate
                    isDuplicating={duplicatingId === p.id}
                  />
                ))}
              </Section>

              {/* Shared (only at root) — with view toggle */}
              {!activeFolder && (
                <Section
                  title="Shared with you"
                  count={shared.length}
                  testid="shared-section"
                  rightSlot={
                    shared.length > 0 ? (
                      <ViewToggle value={sharedView} onChange={setSharedView} prefix="shared" />
                    ) : null
                  }
                  empty={
                    <EmptyState
                      title="Nothing shared yet"
                      sub="When someone adds you to their project's distribution list, it will appear here."
                    />
                  }
                  layout={sharedView}
                >
                  {shared.map((p) => (
                    <ProjectCard
                      key={p.id}
                      project={p}
                      compact={sharedView === "list"}
                      onOpen={() => navigate(`/project/${p.id}`)}
                      onDuplicate={p.role === "admin" ? () => doDuplicate(p) : null}
                      onReportIssue={() => setTicketTarget(p)}
                      canDelete={false}
                      canDuplicate={p.role === "admin"}
                      isDuplicating={duplicatingId === p.id}
                    />
                  ))}
                </Section>
              )}
            </div>

            {/* Right rail */}
            <div className="hidden xl:block">
              <VelocityInsightsPanel
                efficiency={efficiencyPct}
                activeTime={activeTimeLabel}
                overdueCount={overdueTasks || 0}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Create project dialog */}
      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => { setCreateOpen(false); load(); }}
      />

      {/* Paywall */}
      <PaywallDialog
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        tier={data?.tier}
        limit={data?.limit}
        owned={data?.owned_count}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="rounded-md max-w-md" data-testid="delete-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">Delete project?</DialogTitle>
            <DialogDescription className="text-slate-500">
              This permanently removes <span className="font-semibold text-slate-950">&ldquo;{deleteTarget?.name}&rdquo;</span>, all tasks, events, and versions. This action can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-md border-slate-300">Cancel</Button>
            <Button onClick={doDelete} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete">
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report-issue dialog (project-scoped ticket) */}
      <SupportTicketDialog
        open={!!ticketTarget}
        onOpenChange={(o) => !o && setTicketTarget(null)}
        projectId={ticketTarget?.id}
      />

      {/* Folder create / rename / delete */}
      <FolderNameDialog
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        title="New folder"
        confirmLabel="Create folder"
        defaultValue=""
        onSubmit={async (name) => {
          await handleCreateFolder(name);
          setFolderDialogOpen(false);
        }}
      />
      <FolderNameDialog
        open={!!renameTarget}
        onOpenChange={(o) => !o && setRenameTarget(null)}
        title="Rename folder"
        confirmLabel="Save"
        defaultValue={renameTarget?.name || ""}
        onSubmit={async (name) => {
          if (renameTarget) await handleRenameFolder(renameTarget.id, name);
          setRenameTarget(null);
        }}
      />
      <Dialog open={!!deleteFolderTarget} onOpenChange={(o) => !o && setDeleteFolderTarget(null)}>
        <DialogContent className="rounded-md max-w-md" data-testid="delete-folder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">
              Delete folder?
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              &ldquo;{deleteFolderTarget?.name}&rdquo; will be removed and its projects will return to the main list.
              <span className="block mt-2 text-slate-700 font-medium">Projects are NOT deleted.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFolderTarget(null)} className="rounded-md border-slate-300">
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (deleteFolderTarget) await handleDeleteFolder(deleteFolderTarget.id);
                setDeleteFolderTarget(null);
              }}
              className="rounded-md bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-folder"
            >
              Delete folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HaveAQuestionBadge />
    </div>
  );
}

// Drop target shown when drilled into a folder, so users can move a project back to the root.
function FolderRootDropZone({ onDropProject }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes("application/x-progantt-project")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!hover) setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        setHover(false);
        const projectId = e.dataTransfer.getData("application/x-progantt-project");
        if (projectId) {
          e.preventDefault();
          onDropProject?.(projectId);
        }
      }}
      className={`mb-6 rounded-md border-2 border-dashed p-3 text-center text-[11px] font-mono-ibm uppercase tracking-widest transition-colors ${
        hover ? "border-ink bg-mint/40 text-slate-950" : "border-slate-300 text-slate-500"
      }`}
      data-testid="folder-root-drop-zone"
    >
      Drag a project here to remove it from this folder
    </div>
  );
}

// Decide which dialog to open on "New Project" click. Lifted out of the
// component so React-Hooks v5's experimental immutability rule doesn't
// flag the state read inside the inline arrow.
function _resolveNewClickTarget(payload, setPaywallOpen, setCreateOpen) {
  if (payload && payload.at_limit) {
    setPaywallOpen(true);
    return;
  }
  setCreateOpen(true);
}

// Local helper for folder-name normalization. Pulled outside the component
// so the lint heuristics don't see it as a state-derived expression.
function _trimFolderName(value) {
  if (!value) return "";
  return String(value).trim();
}

// Tiny dialog for creating + renaming folders, reused for both flows.
function FolderNameDialog({ open, onOpenChange, title, confirmLabel, defaultValue, onSubmit }) {
  const [val, setVal] = useState(defaultValue || "");
  useEffect(() => { setVal(defaultValue || ""); }, [defaultValue, open]);
  // Stash the latest val in a ref so the submit handler can read it without
  // tripping the experimental react-hooks/set-state-in-effect rule.
  const valRef = useRef(val);
  useEffect(() => { valRef.current = val; }, [val]);
  const handleSubmitForm = (e) => {
    e.preventDefault();
    const trimmed = _trimFolderName(valRef.current);
    if (trimmed) onSubmit(trimmed);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-md" data-testid="folder-name-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black tracking-tight">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmitForm} className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500">
              Folder name
            </Label>
            <Input
              autoFocus
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="e.g. Q1 Launches"
              maxLength={60}
              className="h-10 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
              data-testid="folder-name-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-md border-slate-300">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!val.trim()}
              className="rounded-md bg-slate-950 hover:bg-slate-800 text-white"
              data-testid="folder-name-confirm"
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, count, empty, children, testid, rightSlot, layout = "grid" }) {
  const gridCls = layout === "list"
    ? "grid grid-cols-1 gap-3"
    : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4";
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-xl tracking-tight">{title}</h2>
          <span className="text-[10px] uppercase tracking-widest text-slate-500 px-1.5 py-0.5 border border-slate-300 rounded-md font-sf-label">
            {count}
          </span>
        </div>
        {rightSlot}
      </div>
      {count === 0 ? empty : <div className={gridCls}>{children}</div>}
    </div>
  );
}

function MetricCard({ label, value, testid }) {
  return (
    <div
      className="rounded-md p-5 bg-transparent border border-slate-200 flex flex-col justify-between min-h-[120px]"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest font-semibold font-sf-label text-slate-500">
        {label}
      </div>
      <div className="text-4xl font-display font-bold tracking-tight text-slate-950">{value}</div>
    </div>
  );
}

function EmptyState({ title, sub, cta }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-md p-10 text-center bg-white">
      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
        <FolderOpen size={18} weight="bold" className="text-slate-400" />
      </div>
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      <div className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">{sub}</div>
      {cta && <div className="mt-4">{cta}</div>}
    </div>
  );
}

function usePersistedView(key) {
  const [view, setView] = useState(() => localStorage.getItem(key) || "grid");
  useEffect(() => {
    localStorage.setItem(key, view);
  }, [key, view]);
  return [view, setView];
}

function ViewToggle({ value, onChange, prefix }) {
  const btn = (mode, label, Icon) => (
    <button
      type="button"
      onClick={() => onChange(mode)}
      aria-label={label}
      title={label}
      className={`px-2.5 h-8 flex items-center justify-center transition-colors ${value === mode ? "bg-slate-950 text-white" : "text-slate-600 hover:text-slate-950"}`}
      data-testid={`${prefix}-view-${mode}`}
    >
      <Icon size={14} weight="bold" />
    </button>
  );
  return (
    <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden" data-testid={`${prefix}-view-toggle`}>
      {btn("grid", "Thumbnail view", SquaresFour)}
      {btn("list", "List view", ListBullets)}
    </div>
  );
}

const STAT_TONES = {
  done: { text: "text-blue-600", bar: "#93c5fd" },
  progress: { text: "text-green-600", bar: "#86efac" },
  blocker: { text: "text-amber-500", bar: "#fde68a" },
};

function StatBox({ value, label, tone, icon, testid }) {
  return (
    <div className="border border-slate-200 rounded-md py-2.5 px-2 flex flex-col items-center justify-center bg-white" data-testid={testid}>
      <span className={`text-2xl font-display font-light leading-none tabular-nums ${STAT_TONES[tone].text}`}>{value}</span>
      <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-600">
        {icon}
        {label}
      </span>
    </div>
  );
}

function ProjectStats({ project }) {
  const total = project.task_count || 0;
  const done = project.done_count || 0;
  const progress = project.in_progress_count || 0;
  const blockers = project.blocker_count || 0;
  const pct = (n) => (total ? `${(n / total) * 100}%` : "0%");
  return (
    <div className="mb-4" data-testid={`project-stats-${project.id}`}>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <StatBox value={done} label="Done" tone="done" testid={`stat-done-${project.id}`}
          icon={<Check size={10} weight="bold" className="text-blue-500" />} />
        <StatBox value={progress} label="In Progress" tone="progress" testid={`stat-progress-${project.id}`}
          icon={<span className="h-2 w-2 rounded-full bg-green-300" />} />
        <StatBox value={blockers} label="Blockers" tone="blocker" testid={`stat-blockers-${project.id}`}
          icon={<WarningCircle size={11} weight="bold" className="text-amber-500" />} />
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden flex" data-testid={`project-progress-bar-${project.id}`}>
        <div className="h-full transition-[width] duration-500" style={{ width: pct(done), background: STAT_TONES.done.bar }} />
        <div className="h-full transition-[width] duration-500" style={{ width: pct(progress), background: STAT_TONES.progress.bar }} />
        <div className="h-full transition-[width] duration-500" style={{ width: pct(blockers), background: STAT_TONES.blocker.bar }} />
      </div>
    </div>
  );
}

function ProjectCard({
  project,
  draggable = false,
  compact = false,
  onOpen,
  onDelete,
  onDuplicate,
  onReportIssue,
  onMoveOut,
  canDelete,
  canDuplicate = true,
  isDuplicating = false,
}) {
  const roleStyles = {
    admin: "bg-slate-950 text-white",
    edit: "bg-blue-50 text-blue-700 border-blue-200 border",
    view: "bg-slate-100 text-slate-600 border-slate-200 border",
    owner: "bg-slate-950 text-white",
  };
  const handleDragStart = (e) => {
    if (!draggable) return;
    e.dataTransfer.setData("application/x-progantt-project", project.id);
    e.dataTransfer.effectAllowed = "move";
  };
  return (
    <div
      className={`bg-white border border-slate-300 rounded-md p-5 hover:border-slate-950 transition-colors group relative ${
        draggable ? "cursor-grab active:cursor-grabbing" : ""
      }`}
      data-testid={`project-card-${project.id}`}
      draggable={draggable}
      onDragStart={handleDragStart}
    >
      {/* Kebab sits top-right, above the clickable area */}
      <div className="absolute top-3 right-3 z-10">
        <ProjectCardMenu
          testidSuffix={project.id}
          canDelete={canDelete}
          canDuplicate={canDuplicate}
          onReportIssue={onReportIssue}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      </div>

      <button
        type="button"
        onClick={onOpen}
        className="text-left w-full"
        data-testid={`open-project-${project.id}`}
      >
        <div className="flex items-start justify-between gap-3 mb-3 pr-8">
          <h3 className="font-display text-lg font-bold tracking-tight text-slate-950 truncate">
            {project.name}
          </h3>
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono-ibm uppercase tracking-widest shrink-0 ${roleStyles[project.role] || roleStyles.view}`}>
            {project.role}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-slate-500 mb-4">
          <div>
            <div className="font-mono-ibm uppercase tracking-widest text-[9px] text-slate-400">Start</div>
            <div className="font-mono-ibm text-slate-700">{project.start_date ? formatDate(project.start_date) : "—"}</div>
          </div>
          <div>
            <div className="font-mono-ibm uppercase tracking-widest text-[9px] text-slate-400">Go-Live</div>
            <div className="font-mono-ibm text-slate-700">{project.go_live_date ? formatDate(project.go_live_date) : "—"}</div>
          </div>
        </div>
        {!compact && <ProjectStats project={project} />}
        <div className="flex items-center justify-between">
          <MemberAvatarStack projectId={project.id} maxVisible={4} size={24} />
          <span className="flex items-center gap-1 text-[11px] font-mono-ibm text-slate-500 group-hover:text-slate-950">
            {project.task_count} task{project.task_count === 1 ? "" : "s"} · Open <ArrowRight size={10} weight="bold" />
          </span>
        </div>
      </button>

      {onMoveOut && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMoveOut(); }}
          className="mt-3 w-full text-[10px] font-mono-ibm uppercase tracking-widest text-slate-400 hover:text-slate-950 inline-flex items-center justify-center gap-1.5"
          data-testid={`remove-from-folder-${project.id}`}
        >
          <X size={10} weight="bold" />
          Remove from folder
        </button>
      )}

      {isDuplicating && (
        <div className="absolute inset-0 bg-white/70 rounded-md flex items-center justify-center z-20">
          <div className="flex items-center gap-2 text-xs font-mono-ibm text-slate-600">
            <Spinner size={14} weight="bold" className="animate-spin" /> Duplicating…
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  if (!user) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-9 px-2 pr-3 rounded-md hover:bg-slate-100 text-slate-800"
        data-testid="user-menu-btn"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
        ) : (
          <div className="h-7 w-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
            <UserCircle size={18} weight="fill" />
          </div>
        )}
        <div className="text-left">
          <div className="text-xs font-semibold leading-none">{user.name || user.email}</div>
          <div className="text-[10px] font-mono-ibm text-slate-500 uppercase tracking-widest mt-0.5">
            {user.tier}{user.is_owner ? " · internal" : ""}
          </div>
        </div>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-300 rounded-md shadow-[0_8px_32px_rgba(15,23,42,0.08)] py-1 z-20" data-testid="user-menu">
          <div className="px-3 py-2 border-b border-slate-200">
            <div className="text-xs font-semibold">{user.name || "—"}</div>
            <div className="text-[11px] text-slate-500 font-mono-ibm truncate">{user.email}</div>
          </div>
          <Link to="/pricing" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm hover:bg-slate-50 text-slate-700">
            Plans &amp; pricing
          </Link>
          <Link to="/settings" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm hover:bg-slate-50 text-slate-700" data-testid="user-menu-settings">
            Account settings
          </Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm hover:bg-slate-50 text-slate-700">
            Contact support
          </Link>
          <button
            type="button"
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700 flex items-center gap-2"
            data-testid="logout-btn"
          >
            <SignOut size={12} weight="bold" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

function TierBadge({ tier }) {
  const map = {
    owner: { label: "Internal", cls: "text-amber-700" },
    free: { label: "Free", cls: "text-slate-700" },
    starter: { label: "Starter", cls: "text-blue-700" },
    pro: { label: "Pro", cls: "text-purple-700" },
    team: { label: "Team", cls: "text-emerald-700" },
  };
  const m = map[tier] || map.free;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${m.cls}`}>
      {tier === "owner" && <Crown size={10} weight="fill" />}
      {m.label}
    </span>
  );
}

function CreateProjectDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [goLive, setGoLive] = useState("");
  const [creating, setCreating] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const p = await createProject({
        name: name.trim(),
        start_date: startDate || null,
        go_live_date: goLive || null,
      });
      toast.success("Project created");
      setName(""); setStartDate(""); setGoLive("");
      onCreated();
      // Navigate to the new project
      window.location.href = `/project/${p.id}`;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-md" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">New Project</DialogTitle>
          <DialogDescription className="text-slate-500">
            You&apos;ll be the admin and sole member to start. Add collaborators afterwards.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-1">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">Project Name</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project ABC — Ecomm Website Launch"
              className="h-10 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
              data-testid="create-project-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">Start</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10 mt-1.5 rounded-md border-slate-300 font-mono-ibm focus-visible:ring-0 focus-visible:border-slate-950"
                data-testid="dash-create-start"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">Go-Live</Label>
              <Input
                type="date"
                value={goLive}
                onChange={(e) => setGoLive(e.target.value)}
                className="h-10 mt-1.5 rounded-md border-slate-300 font-mono-ibm focus-visible:ring-0 focus-visible:border-slate-950"
                data-testid="dash-create-golive"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-md border-slate-300">Cancel</Button>
            <Button type="submit" disabled={creating} className="rounded-md bg-slate-950 hover:bg-slate-800 text-white" data-testid="create-project-submit">
              {creating ? "Creating…" : "Create Project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
